import { useCallback, useEffect, useRef, useState } from "react";
import type { Scenario, ScenarioStep } from "../lib/scenario";
import { SCENARIO_GENERATORS } from "../lib/scenarioGenerators";

export interface ScenarioState {
  running: boolean;
  stepIndex: number;        // -1 = idle, 0..n-1 = playing, n = done
  step: ScenarioStep | null;
  done: boolean;
  events: ScenarioStep[];   // accumulated events for activity feed
  totalSteps: number;       // length of the live-generated run
}

export interface ScenarioControls {
  play: (scenario: Scenario) => void;
  pause: () => void;
  reset: () => void;
}

export function useScenario(): [ScenarioState, ScenarioControls] {
  const [state, setState] = useState<ScenarioState>({
    running: false,
    stepIndex: -1,
    step: null,
    done: false,
    events: [],
    totalSteps: 0,
  });
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stepsRef = useRef<ScenarioStep[]>([]);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const advance = useCallback((idx: number) => {
    const steps = stepsRef.current;
    if (idx >= steps.length) {
      setState((s) => ({ ...s, running: false, done: true, stepIndex: steps.length, step: null, totalSteps: steps.length }));
      return;
    }
    const step = steps[idx];
    if (!step) return;
    setState((s) => ({
      running: true,
      stepIndex: idx,
      step,
      done: false,
      events: [...s.events, step],
      totalSteps: steps.length,
    }));
    timerRef.current = setTimeout(() => advance(idx + 1), step.durationMs);
  }, []);

  const play = useCallback((scenario: Scenario) => {
    clearTimer();
    // Live-generated steps (walks the project graph + env thresholds)
    // are the source of truth. Fallback to scripted steps only if the
    // generator returns empty — a real graph should never produce zero.
    const gen = SCENARIO_GENERATORS[scenario.id];
    const generated = gen ? gen() : [];
    stepsRef.current = generated.length > 0 ? generated : scenario.steps;
    setState({ running: true, stepIndex: -1, step: null, done: false, events: [], totalSteps: stepsRef.current.length });
    timerRef.current = setTimeout(() => advance(0), 250);
  }, [advance, clearTimer]);

  const pause = useCallback(() => {
    clearTimer();
    setState((s) => ({ ...s, running: false }));
  }, [clearTimer]);

  const reset = useCallback(() => {
    clearTimer();
    stepsRef.current = [];
    setState({ running: false, stepIndex: -1, step: null, done: false, events: [], totalSteps: 0 });
  }, [clearTimer]);

  useEffect(() => () => clearTimer(), [clearTimer]);

  return [state, { play, pause, reset }];
}
